import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(2deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(60px); }
  to { opacity: 1; transform: translateY(0); }
`;

const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const HeroContainer = styled(Box)({
  minHeight: '100vh',
  background: '#0a0a0f',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  padding: '120px 40px 80px',
  '@media (max-width: 768px)': {
    padding: '100px 20px 60px',
    minHeight: 'auto'
  }
});

const GradientOrb = styled(Box)({
  position: 'absolute',
  width: '800px',
  height: '800px',
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(0, 212, 255, 0.15) 0%, transparent 70%)',
  top: '-20%',
  right: '-10%',
  animation: `${pulse} 8s ease-in-out infinite`,
  pointerEvents: 'none'
});

const GradientOrb2 = styled(Box)({
  position: 'absolute',
  width: '600px',
  height: '600px',
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(124, 58, 237, 0.12) 0%, transparent 70%)',
  bottom: '-30%',
  left: '-10%',
  animation: `${pulse} 10s ease-in-out infinite`,
  animationDelay: '2s',
  pointerEvents: 'none'
});

const GridPattern = styled(Box)({
  position: 'absolute',
  inset: 0,
  backgroundImage: `
    linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px)
  `,
  backgroundSize: '60px 60px',
  pointerEvents: 'none'
});

const ContentWrapper = styled(Box)({
  maxWidth: '1400px',
  margin: '0 auto',
  width: '100%',
  display: 'grid',
  gridTemplateColumns: '1.1fr 0.9fr',
  gap: '80px',
  alignItems: 'center',
  position: 'relative',
  zIndex: 10,
  '@media (max-width: 1024px)': {
    gridTemplateColumns: '1fr',
    gap: '60px'
  }
});

const LeftContent = styled(Box)({
  animation: `${slideUp} 1s ease-out`
});

const StatusBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '10px',
  padding: '10px 20px',
  background: 'rgba(0, 212, 255, 0.1)',
  border: '1px solid rgba(0, 212, 255, 0.2)',
  borderRadius: '100px',
  marginBottom: '32px'
});

const StatusDot = styled(Box)({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  background: '#00d4ff',
  boxShadow: '0 0 10px #00d4ff'
});

const StatusText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  fontWeight: 500,
  color: '#00d4ff',
  textTransform: 'uppercase',
  letterSpacing: '1.5px'
});

const Headline = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontSize: 'clamp(48px, 8vw, 84px)',
  fontWeight: 800,
  lineHeight: 1,
  letterSpacing: '-3px',
  color: '#ffffff',
  marginBottom: '28px'
});

const GradientSpan = styled('span')({
  background: 'linear-gradient(135deg, #00d4ff 0%, #7c3aed 50%, #f97316 100%)',
  backgroundSize: '200% 200%',
  animation: `${gradientMove} 5s ease infinite`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text'
});

const Description = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '18px',
  fontWeight: 400,
  color: 'rgba(255, 255, 255, 0.6)',
  lineHeight: 1.8,
  maxWidth: '520px',
  marginBottom: '48px',
  '@media (max-width: 768px)': {
    fontSize: '16px'
  }
});

const ButtonGroup = styled(Box)({
  display: 'flex',
  gap: '16px',
  flexWrap: 'wrap'
});

const PrimaryButton = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '12px',
  padding: '18px 36px',
  background: 'linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%)',
  borderRadius: '16px',
  cursor: 'pointer',
  fontFamily: "'Inter', sans-serif",
  fontSize: '16px',
  fontWeight: 600,
  color: '#ffffff',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 8px 32px rgba(0, 212, 255, 0.3)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 16px 48px rgba(0, 212, 255, 0.4)'
  }
});

const SecondaryButton = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '12px',
  padding: '18px 36px',
  background: 'rgba(255, 255, 255, 0.04)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '16px',
  cursor: 'pointer',
  fontFamily: "'Inter', sans-serif",
  fontSize: '16px',
  fontWeight: 600,
  color: '#ffffff',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.08)',
    borderColor: 'rgba(255, 255, 255, 0.2)'
  }
});



const RightContent = styled(Box)({
  position: 'relative',
  animation: `${slideUp} 1s ease-out`,
  animationDelay: '0.2s',
  animationFillMode: 'backwards',
  '@media (max-width: 1024px)': {
    display: 'none'
  }
});

const ImageContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  aspectRatio: '4/5',
  borderRadius: '32px',
  overflow: 'hidden',
  animation: `${float} 8s ease-in-out infinite`
});

const HeroImage = styled(Box)({
  width: '100%',
  height: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'center'
});

const ImageOverlay = styled(Box)({
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(180deg, rgba(10, 10, 15, 0) 0%, rgba(10, 10, 15, 0.6) 100%)'
});

const ImageBorder = styled(Box)({
  position: 'absolute',
  inset: '-2px',
  borderRadius: '34px',
  padding: '2px',
  background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.5), rgba(124, 58, 237, 0.5), rgba(249, 115, 22, 0.5))',
  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
  WebkitMaskComposite: 'xor',
  maskComposite: 'exclude',
  pointerEvents: 'none'
});

const FloatingCard = styled(Box)({
  position: 'absolute',
  bottom: '40px',
  left: '-60px',
  background: 'rgba(18, 18, 26, 0.9)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: '20px',
  padding: '20px 28px',
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)'
});

const CardIcon = styled(Box)({
  width: '48px',
  height: '48px',
  borderRadius: '14px',
  background: 'linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#ffffff'
});

const CardContent = styled(Box)({});

const CardTitle = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 600,
  color: '#ffffff'
});

const CardSubtitle = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '12px',
  color: 'rgba(255, 255, 255, 0.5)'
});

// Helper to extract URL from various photo object formats
const getImageUrl = (image) => {
  if (!image) return null;
  if (typeof image === 'string') return image;
  return image.url || image.googleUrl || image.sourceUrl || image.image || image.imageUrl || image.thumbnailUrl || null;
};

const HeroSection = ({ businessInfo, heroImage }) => {
  const defaultImage = 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1200&q=80';
  const resolvedHeroImage = getImageUrl(heroImage) || defaultImage;

  return (
    <HeroContainer id="home">
      <GradientOrb />
      <GradientOrb2 />
      <GridPattern />

      <ContentWrapper>
        <LeftContent>
          <StatusBadge>
            <StatusDot />
            <StatusText>Available Now</StatusText>
          </StatusBadge>

          <Headline>
            Welcome to<br />
            <GradientSpan>{businessInfo?.name || 'Our Business'}</GradientSpan>
          </Headline>

          <Description>
            {businessInfo?.description ||
              'Experience excellence in everything we do. We deliver premium services tailored to your unique needs with passion and precision.'}
          </Description>

          <ButtonGroup>
            <PrimaryButton onClick={() => document.querySelector('#services')?.scrollIntoView({ behavior: 'smooth' })}>
              Explore Services
              <ArrowOutwardIcon sx={{ fontSize: 18 }} />
            </PrimaryButton>
            <SecondaryButton onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}>
              <PlayArrowIcon sx={{ fontSize: 20 }} />
              Contact Us
            </SecondaryButton>
          </ButtonGroup>


        </LeftContent>

        <RightContent>
          <ImageContainer>
            <HeroImage sx={{ backgroundImage: `url(${resolvedHeroImage})` }} />
            <ImageOverlay />
            <ImageBorder />
          </ImageContainer>


        </RightContent>
      </ContentWrapper>
    </HeroContainer>
  );
};

export default HeroSection;
